import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { roleFromToken } from "../hooks/useCurrentUser";
import logo from "../assets/imgiconlogo.png";
import { HASBRO_COMPANY_ID, isHasbroEmail } from "../components/hasbro/HasbroGuard";
import { HASBRO_BRAND } from "../components/hasbro/hasbroTheme";
import SmsCodeStep from "../components/security/SmsCodeStep";
import { needsSecondFactor, resolverFor } from "../auth/mfa";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Hasbro look for /login?brand=hasbro (Hasbro links) or as soon as a Hasbro email is typed
  const hasbro = params.get("brand") === "hasbro" || isHasbroEmail(email);

  // Accounts with two-step sign-in: the password step returns a resolver for the SMS step
  const [mfaResolver, setMfaResolver] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      // 🔐 Sign in with Firebase Auth
      await afterSignIn(await signInWithEmailAndPassword(auth, email, password));
    } catch (err) {
      if (needsSecondFactor(err)) { setMfaResolver(resolverFor(err)); return; }
      console.error("❌ Login error:", err.code || err.message);
      setError("Login failed. Please check your email or password.");
    }
  };

  // Load the user's record and open their workspace (after the password, or the SMS code)
  const afterSignIn = async (userCredential) => {
    setError("");
    try {
      const uid = userCredential.user.uid;

      // 🔎 Fetch Firestore user record — if Firestore can't be reached (e.g. its daily read
      // quota), the login token's claims carry the role and company
      let userDoc;
      try {
        userDoc = await getDoc(doc(db, "users", uid));
      } catch (readErr) {
        const claims = await roleFromToken(userCredential.user, true);
        if (!claims) throw readErr;
        userDoc = { exists: () => true, data: () => claims };
      }
      if (!userDoc.exists()) throw new Error("User not found in Firestore.");

      const userData = userDoc.data();
      const userRole = userData.role;
      let userClientId = userData.companyId || userData.clientId;
      if (userRole === "client" && !userClientId) {
        userClientId = "nestle"; // Fallback for testing
      }

      console.log("🔥 Firestore user data:", userData);

      // Roles that don't require a companyId to log in
      const noCompanyRoles = ["super-admin", "operator", "agent", "vendor", "hasbro-customer"];
      if (!userRole || (!noCompanyRoles.includes(userRole) && !userClientId)) {
        throw new Error("Missing role or clientId in Firestore.");
      }

      // 🧠 Store in localStorage for future access
      if (userClientId) localStorage.setItem("clientId", userClientId);
      localStorage.setItem("role", userRole);
      localStorage.setItem("email", email);

      // 🚀 Redirect based on role (Hasbro accounts go to their own workspace)
      if (userClientId === HASBRO_COMPANY_ID && isHasbroEmail(userCredential.user.email)) navigate("/hasbro");
      else if (userRole === "hasbro-customer") navigate("/hasbro-customer");
      else if (userRole === "super-admin") navigate("/super-admin");
      else if (userRole === "admin") navigate("/admin");
      else if (userRole === "client") navigate("/client");
      else if (userRole === "customer") navigate("/client");
      else if (userRole === "operator") navigate("/operator");
      else if (userRole === "agent") navigate("/agent");
      else if (userRole === "3pl") navigate("/3pl");
      else if (userRole === "vendor") navigate("/vendor");
      else throw new Error("Unknown role: " + userRole);

    } catch (err) {
      console.error("❌ Login error:", err.message);
      setMfaResolver(null);
      setError(err.message.includes("Firestore") || err.message.includes("clientId") || err.message.includes("role")
        ? "Access denied: Your account is not correctly configured."
        : "Login failed. Please check your email or password.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 relative overflow-hidden transition-[background] duration-500" style={{ background: hasbro ? HASBRO_BRAND.pageBg : 'radial-gradient(circle at 30% 40%, #0b2a42, #071d2e)' }}>
      {/* dot pattern */}
      <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
      <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md space-y-6">
        <div className="flex flex-col items-center">
          <img
            src={hasbro ? HASBRO_BRAND.logo : logo}
            alt={hasbro ? "Hasbro" : "TinyGlobal Logo"}
            className={`w-20 h-20 shadow-md ${hasbro ? "rounded-2xl object-cover" : "rounded-full object-contain"}`}
          />
          <h2 className="text-2xl font-bold text-brand-dark mt-4">Welcome Back</h2>
          {hasbro && <p className="text-xs text-slate-400 mt-1">Hasbro Logistics Portal</p>}
        </div>

        {mfaResolver ? (
          <SmsCodeStep
            resolver={mfaResolver}
            onDone={afterSignIn}
            onCancel={() => { setMfaResolver(null); setPassword(""); }}
            accent={hasbro ? "bg-[#0087C9] hover:bg-[#0065A0]" : "bg-teal-600 hover:bg-teal-700"}
          />
        ) : (
        <form className="space-y-4" onSubmit={handleLogin}>
          {error && (
            <p className="text-sm text-red-600 font-semibold">{error}</p>
          )}

          <input
            type="email"
            placeholder="Email"
            className="w-full p-3 border border-gray-300 rounded-md"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <input
            type="password"
            placeholder="Password"
            className="w-full p-3 border border-gray-300 rounded-md"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          <button
            type="submit"
            className={`w-full text-white p-3.5 rounded-xl font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 active:scale-[0.98] ${hasbro ? "bg-gradient-to-r from-[#00B7EB] to-[#0065A0] shadow-[#0087C9]/30" : "bg-gradient-to-r from-teal-500 to-emerald-600 shadow-teal-500/20 hover:shadow-teal-500/30"}`}
          >
            Sign In
          </button>
        </form>
        )}

        <div className="text-sm text-center text-gray-400 pt-2">
          <p>
            Forgot password? Contact{" "}
            <a href="mailto:contact@yadata.com.au" className="text-teal-600 font-semibold hover:underline">
              contact@yadata.com.au
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
